import React, { useEffect, useRef, useState } from 'react';
import FloatingBox from './FloatingBox';
import { philosophyBoxes } from '../constants/data';
import { philosophyOverlay } from '../constants/assets';

export default function PhilosophySection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  // Sequential box reveal
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          observer.disconnect();
          setVisibleCount(1);
          setTimeout(() => setVisibleCount(2), 420);
          setTimeout(() => setVisibleCount(3), 840);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Draw video + tint blend to canvas for consistent rendering.
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    const width = canvas.width;
    const height = canvas.height;
    const whiteCutoff = 200;

    const drawFrame = () => {
      if (video.readyState >= 2) {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw > 0 && vh > 0) {
          // Object-cover crop so canvas keeps the intended composition.
          const scale = Math.max(width / vw, height / vh);
          const dw = vw * scale;
          const dh = vh * scale;
          const dx = (width - dw) / 2;
          const dy = (height - dh) / 2;

          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(video, dx, dy, dw, dh);

          // Key out near-white pixels so white background becomes transparent.
          const frame = ctx.getImageData(0, 0, width, height);
          const data = frame.data;
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i] ?? 0;
            const g = data[i + 1] ?? 0;
            const b = data[i + 2] ?? 0;
            const brightness = (r + g + b) / 3;
            if (brightness >= whiteCutoff) {
              data[i + 3] = 0;
            }
          }
          ctx.putImageData(frame, 0, 0);
        }
      }
      rafId = requestAnimationFrame(drawFrame);
    };

    rafId = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <section
      id="our-philosophy"
      className="philosophy-section relative flex flex-col gap-8 items-end justify-center p-8 w-full mx-auto"
      ref={containerRef}
    >
      {/* Canvas-rendered blended video */}
      <div className="philosophy-video-layer pointer-events-none">
        <canvas
          ref={canvasRef}
          width={764}
          height={764}
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        />
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 h-full w-full object-cover opacity-0"
        >
          <source src="/src/assets/rodin360.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Background overlay */}
      <img
        src={philosophyOverlay}
        alt=""
        loading="lazy" 
        className="philosophy-overlay absolute pointer-events-none"
      />

      {/* Quote */}
      <div className="flex flex-col gap-4 items-center justify-end max-w-[542px] min-w-[300px] w-full text-black">
        <p className="font-trispace type-section-label w-full">Our philosophy</p>
        <blockquote className="font-trispace type-h2 w-full">
          "A few skilled individuals with the right tools can now do what used to demand an entire team of experts."
        </blockquote>
      </div>

      {/* Cascading dark boxes */}
      <div className="philosophy-boxes-scroll relative shrink-0 w-full">
        <div className="philosophy-boxes-track relative">
          {philosophyBoxes.map((box, i) => (
            <FloatingBox
              key={box.title}
              {...box}
              theme="dark"
              visible={visibleCount > i}
              className="philosophy-box"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
